import { REGISTRATION_FORM_DEFINITION_ID } from '@/constants/constants';
import { AdminContext } from '@/contexts/AdminContext';
import useMemberDetails from '@/hooks/useMemberDetails';
import { getLookupIdFromValue } from '@/utils/lookupUtils';
import { faChevronDown, faCopy } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoadingButton } from '@mui/lab';
import { Accordion, Alert, Box, Button, CircularProgress, Grid, Paper, Snackbar, Step, StepContent, StepLabel, Stepper, Typography } from '@mui/material';
import { ChosenThemeActionContext, Form, FormSubmitButton, FormsFeatureContext, IValidationError, useFormLookups, useFormMetadata, useFormValidate } from '@ngt/forms';
import { IFormSubmit, ValidateOn, useFormActions, useFormState, useScopedField } from '@ngt/forms-core';
import { enqueueSnackbar } from 'notistack';
import React, { FC, useContext, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import client from '../../api/client';
import * as Dtos from '../../api/dtos';
import DeleteModal from '../common/DeleteModal';
import Invoice from '../registration/Invoice';
import PackageProvider from '../registration/packages/PackageProvider';
import RegistrationCostUpdater from '../RegistrationCostUpdater';
import RegistrationTypeUpdater from '../shared/RegistrationTypeUpdater';
import AccommodationSectionUpdate from '../speaker/SpeakerAccommodationSection';
import { StyledAccordianSummary, StyledAccordionDetails } from '../speaker/SpeakerForm';
import SpeakerGuideSection from '../speaker/SpeakerGuideSection';
import SpeakerSection from '../speaker/SpeakerSection';
import SpeakerTravelSection from '../speaker/SpeakerTravelSection';
import AdminNavigation from "./AdminNavigation";
import AdminRegistrationSteps from './AdminRegistrationSteps';

const initialValue =  {attendanceType: 1, registrationType: 1} as Dtos.Registration & { alreadyRegistered?: boolean; registrationType: number };

const subscription = {
    value: true
};

const HeardAboutAsmUpdater = () => {

    const {
        state: {
            value: heardAboutAsm
        },
        context: {
            setValue: setHeardAboutAsm        }
    } = useScopedField<number>('heardAboutAsm', subscription);

    const {
        state: {
            value: heardAboutAsmOther
        },
        context: {
            setValue: setHeardAboutAsmOther        }
    } = useScopedField<string>('heardAboutAsmOther', subscription);

    useEffect(() => {
        if (heardAboutAsm === null || heardAboutAsm === undefined || (heardAboutAsm === 22 && (heardAboutAsmOther === null || heardAboutAsmOther === undefined))) {
            setHeardAboutAsm(22);
            setHeardAboutAsmOther("N/A");
        }
    }, [heardAboutAsm, heardAboutAsmOther])

    return null;
}

const FormSaving = () => {
    const {submitting} = useFormState({submitting: true});

    if (!submitting) {
        return null;
    }

    return (
        <Box
            sx={{
                position: 'fixed',
                background: 'rgba(0,0,0,0.25)',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: '80px',
                    height: '80px',
                    margin: 'auto'
                }}
            >
                <CircularProgress size={80} />
            </Box>
        </Box>
    );
}

/**
 * This whole garbage component is just to convert from the state string in members to the state id in the form which is a lookup, I think.
 * @returns 
 */
const MemberDetailUpdate = () => {

    const {setValue, getValue} = useFormActions<Dtos.Registration>();

    const {state: {value: firstName}} = useScopedField<string>('firstName', {value: true});
    const {state: {value: lastName}} = useScopedField<string>('lastName', {value: true});

    const {data: memberDetails} = useMemberDetails(firstName ?? undefined, lastName ?? undefined, { revalidateOnFocus: false, refreshWhenOffline: false, });

    const formMetadata = useFormMetadata(REGISTRATION_FORM_DEFINITION_ID);
    const {data:lookups} = useFormLookups(formMetadata, { revalidateOnFocus: false, refreshWhenOffline: false, });

    useEffect(() => {

        if (!memberDetails?.firstName){
            return;
        }

        const formValue = getValue();

        if (formValue?.id) {
            return;
        }

        setValue(currentValue => {
            const {state, ...memberDetailsMinusState} = memberDetails;

            return {
                ...currentValue,
                ...memberDetailsMinusState,
                state: getLookupIdFromValue(lookups, 'state', state)
            }
        }, true, true, true, false);

    }, [memberDetails, setValue, getValue]);

    return null;
}

const AdminRegistrationForm: FC = () => {
    const setChosenTheme = useContext(ChosenThemeActionContext);

    const formMetadata = useFormMetadata(REGISTRATION_FORM_DEFINITION_ID);
    const {data:lookups} = useFormLookups(formMetadata, { revalidateOnFocus: false, refreshWhenOffline: false, });
    const validate: any = useFormValidate(formMetadata)
    useEffect(() => {
        setChosenTheme('light');
    }, [])
    const {serviceStackClient} = useContext(FormsFeatureContext);

    const [deleteRegistration, setDeleteRegistration] = React.useState<Dtos.Registration | undefined>();
    const [registration, setRegistration] = React.useState<Dtos.Registration | undefined>();
    const [activeStep, setActiveStep] = React.useState(0);
    const [displayInvoice, setDisplayInvoice] = React.useState(false);
    const [allSteps] = React.useState(true);
    const [emailResponse, setEmailResponse] = React.useState<any>();
    const [completed] = React.useState<{
        [k: number]: boolean;
      }>({}); 

    const navigate = useNavigate();
    const invoiceRef = useRef<React.ReactInstance>();

    const handlePrint = useReactToPrint({
        content: () => invoiceRef.current ?? null
    });

    const totalSteps = () => {
        return AdminRegistrationSteps.length;
    };

    const completedSteps = () => {
        return Object.keys(completed).length;
    };

    const isLastStep = () => {
        return activeStep === totalSteps() - 1;
    };

    const allStepsCompleted = () => {
        return completedSteps() === totalSteps();
    };

    const params = useParams();
    const regId = params?.id;
    const isEditingExistingRegistration = regId !== undefined && regId !== '0';
    const [registrationLoadComplete, setRegistrationLoadComplete] = React.useState(!isEditingExistingRegistration);
    
    const onSubmit:IFormSubmit<Dtos.Registration, IValidationError> = async (formState, formActions) => {
        if(!formState.value)
            return;
        
        const paymentSubmit = !!(formState.value as any).submitTriggeredByPayment

        try {

            const response = await serviceStackClient.post(new Dtos.PostRegistrationSave({
                registration: formState.value
            }));

            const newRegistration = response.registration;
            
            formActions.setFieldValue(`payments`, newRegistration?.payments, true, true, true, false);


            setRegistration(newRegistration);

            if (!paymentSubmit) {
                window.location.replace('/admin/registration-desk');
            }
        }
        catch(err){
            if (paymentSubmit) {
                formActions.setFieldValue('paymentFailure', true, true, true, true, false);
            }
        }
    }

    React.useEffect(() => {
        if (!isEditingExistingRegistration) {
            setRegistrationLoadComplete(true);
            return;
        }

        setRegistrationLoadComplete(false);
        if (regId !== undefined && regId !== '0') {
        client
            .get(new Dtos.RegistrationGetSingleById({
                id: parseInt(regId ? regId : ''),
            }))
            .then(response => {
         
                setRegistration(response.data)
            })
            .catch(() => {
                 
            })
            .finally(() => {
                setRegistrationLoadComplete(true);
            });
        }
    }, [client, isEditingExistingRegistration, regId, setRegistration]);

    const handleNext = () => {
        const newActiveStep =
            isLastStep() && !allStepsCompleted()
            ? // It's the last step, but not all steps have been completed,
                // find the first step that has been completed
                AdminRegistrationSteps.findIndex((step, i) => !(i in completed))
            : activeStep + 1;
        setActiveStep(newActiveStep);
    };

    const handleDelete = () => {
        setDeleteRegistration(registration);
    };

    const handleClose = (refetch: boolean) => {
        setDeleteRegistration(undefined);
        if(refetch){
            navigate('/admin/registration-desk');
        }
    }

    const handleSnackClose = () => {
        setEmailResponse(undefined);
    }

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleDisplayInvoice = () => {
        setDisplayInvoice(!displayInvoice);
    };

    const copyUpdateDetailsLink = () => {

        let updateLink;
        if(registration?.delegateType ===  4) // guest speaker
        {
            updateLink = `${window.location.origin}/speaker/${registration?.guid}`;  
        }
        else{
            updateLink = `${window.location.origin}/registration-update/${registration?.guid}`;
        }
        enqueueSnackbar('Registration portal link copied to clipboard!',  {variant: 'success'  });
        navigator.clipboard.writeText(updateLink);

    };

    const [loading, setLoading] = React.useState(false);

    const handleEmailInvoice = async() => {
        setLoading(true);
        try{
            const response = await serviceStackClient.get(new Dtos.QueueInvoiceEmail({
                registrationId: registration!.id,
                voucherId: registration!.voucherId
            }));
            setEmailResponse(response);
        }
        catch(err){
            
        }
        setLoading(false);
    };

    const initialFormData = useMemo(() => {
        if (!registration) {
            return initialValue;
        }
        
        return {
            ...registration,
            registrationType: 
                registration?.trialCoordinator ? 3 :
                registration?.student ? 2 :
                registration?.staff ? 5 :
                registration?.registrar ? 4 :
                registration?.attendeeFromJapanOrKorea ? 6 :
                1
        }
    }, [registration, initialValue])

    return (
        <>
        <AdminNavigation/>
        <DeleteModal 
            registration={deleteRegistration}
            handleModalClose={handleClose}
        />
        <Box sx={{paddingLeft: '100px', paddingRight: '100px', paddingTop:'30px', paddingBottom:'30px'}}>
            <Paper elevation={2} sx={{padding:'40px', pb: 10}}>
                <Grid container spacing={1}>
                    <Grid 
                        item 
                        xs={12}
                        sm={12}
                        md={12}
                        lg={6}
                        xl={9}
                    >
                        <Typography fontFamily='Roboto' color='primary' fontSize='30pt'>Registration {registration?.invoiceNumber && ("("+registration?.invoiceNumber+")")}</Typography>
                    </Grid>
                    {
                        displayInvoice ? 
                            <Grid 
                                item 
                                xs={12} 
                                sm={12}
                                md={12}
                                lg={6}
                                xl={3}
                                sx={{textAlign: 'right'}}
                            >
                                <Button 
                                    onClick={handleDisplayInvoice}
                                    variant="contained"
                                >
                                    Back
                                </Button>
                                <LoadingButton 
                                    loading={loading}
                                    sx={{ml:2}}
                                    onClick={handleEmailInvoice}
                                    variant="contained"
                                >
                                    Email
                                </LoadingButton>
                                <Button
                                    sx={{ml:2}}
                                    variant="contained"
                                    onClick={()=>handlePrint()}
                                >
                                    Print 
                                </Button>
                            </Grid> :
                            <Grid 
                                item 
                                xs={12} 
                                sm={12}
                                md={12}
                                lg={6}
                                xl={3}
                                sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}
                            >
                                {registration && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Button 
                                            onClick={copyUpdateDetailsLink}
                                            variant="contained"
                                            size="large"
                                            sx={{ whiteSpace: 'nowrap', minWidth: 150 }}
                                        >
                                            <div style={{marginRight: 8}}>
                                                <FontAwesomeIcon  icon={faCopy} /> 
                                            </div>
                                            Registration Portal
                                        </Button>
                                        <Button 
                                            onClick={handleDisplayInvoice}
                                            variant="contained"
                                            size="large"
                                            sx={{ whiteSpace: 'nowrap', minWidth: 150 }}
                                        >
                                            View Invoice
                                        </Button>
                                        <Button 
                                            onClick={handleDelete}
                                            variant="contained"
                                            size="large"
                                            sx={{ whiteSpace: 'nowrap', minWidth: 150 }}
                                        >
                                            Delete
                                        </Button>
                                    </Box>
                                )}
                            </Grid>
                    }

                </Grid> 
                {displayInvoice ?
                <Invoice
                    ref={invoiceRef}
                    key={'invoice'+ registration?.id}
                    registration={registration!}
                />
                :
                !registrationLoadComplete ?
                <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box>
                :
                <Form // replace with initialFormData
                    initialValue={initialFormData}
                    debug={import.meta.env.DEV}
                    lookups={lookups}
                    onValidate={validate}
                    validateOn={ValidateOn.onChange}
                    onSubmit={onSubmit}
                >
                    <AdminContext.Provider value={true}>
                        <MemberDetailUpdate />
                        <PackageProvider>
                            <RegistrationCostUpdater />
                            <RegistrationTypeUpdater />
                            <HeardAboutAsmUpdater />

                            <FormSaving />
                        
                            {allSteps ?
                                <>
                                {AdminRegistrationSteps.map(({Inputs}, index) => (
                                    <Box key={index}>
                                        <Inputs />
                                    </Box>
                                ))} 
                                
                                <SpeakerSectionOfRegistration registration={registration}/>

                                <FormSubmitButton variant="contained"
                                    sx={{
                                        float: "right",
                                        marginTop:"20px",
                                    }}
                                >
                                    Submit
                                </FormSubmitButton>
                                </>
                            :
                            <Stepper nonLinear sx={{marginBottom: "20px"}} activeStep={activeStep} orientation="vertical">
                                {AdminRegistrationSteps.map(({label, Inputs}, index) => (
                                    <Step key={label} completed={completed[index]}>
                                        <StepLabel
                                            onClick={() => {
                                                setActiveStep(index);
                                            }}
                                        >
                                        {label}                         
                                        </StepLabel>
                                        
                                        <StepContent>
                                            <Inputs />
                                            {
                                                
                                                activeStep === 0 ?
                                                    <Button 
                                                        onClick={handleNext} 
                                                        variant="contained"
                                                        sx={{
                                                            marginTop:"20px",
                                                            marginRight:"20px",
                                                            float: 'right'
                                                        }}
                                                    >
                                                        Next
                                                    </Button>
                                                : 
                                                activeStep === 3 ?
                                                <Box>
                                                    <FormSubmitButton variant="contained"
                                                        sx={{
                                                            float: "right",
                                                            marginTop:"20px",
                                                        }}
                                                    >
                                                        Submit
                                                    </FormSubmitButton>
                                                    <Button variant="contained" 
                                                        onClick={handleBack}
                                                        sx={{
                                                            marginTop:"20px",
                                                            marginRight:"20px",
                                                            float: 'right'
                                                        }}
                                                    >
                                                        Back
                                                    </Button>
                                                </Box>
                                                :
                                                <Box>
                                                    <Button 
                                                        onClick={handleNext} 
                                                        variant="contained"
                                                        sx={{
                                                            marginTop:"20px",
                                                            marginRight:"20px",
                                                            float: 'right'
                                                        }}
                                                    >
                                                        Next
                                                    </Button>
                                                    <Button 
                                                        variant="contained" 
                                                        onClick={handleBack}
                                                        sx={{
                                                            marginTop:"20px",
                                                            marginRight:"20px",
                                                            float: 'right'
                                                        }}
                                                    >
                                                        Back
                                                    </Button>
                                                
                                                </Box>
                                            }
                                        </StepContent>
                                    </Step>
                                ))}
                            </Stepper>
                            }

                                  

                        </PackageProvider>
                    </AdminContext.Provider>
                </Form>
                }           
            </Paper>
            <Snackbar 
                open={emailResponse} 
                autoHideDuration={5000} 
                onClose={handleSnackClose} 
                anchorOrigin={{vertical:'bottom', horizontal:'right'}}
            >
                <Alert onClose={handleSnackClose} sx={{ width: '100%' }}>
                    Email Sent
                </Alert>
            </Snackbar>
        </Box>
        </>
    )
}

interface SpeakerSectionOfRegistrationProps {
    registration?: Dtos.Registration;
}

const SpeakerSectionOfRegistration: FC<SpeakerSectionOfRegistrationProps> = ({
    registration
}) => {
       
    return (
        <>
            {
                registration?.isSpeaker && (
                    <Accordion 
                        elevation={4}
                    >
                        <StyledAccordianSummary 
                            expandIcon={<FontAwesomeIcon fixedWidth icon={faChevronDown} color='white' />}
                        >
                            <Typography variant="h3" color="white"><b>Speaker's Information</b></Typography>   
                        </StyledAccordianSummary>
                        <StyledAccordionDetails>
                            
                            <SpeakerSection/>
                            
                            <SpeakerGuideSection />
                        
                            <SpeakerTravelSection />
                        
                            <AccommodationSectionUpdate />

                        </StyledAccordionDetails>
                    </Accordion>
                )
            }  
        </>
    )
}


export default AdminRegistrationForm;
